import React from 'react'
import { Link } from '@inertiajs/react'
import { Globe, Monitor, Smartphone, Tablet, ExternalLink } from 'lucide-react'
import styles from './VisitorsTable.module.scss'
import type { AnalyticsVisitorRow } from '@/types/analytics'
import { countryWithFlag } from '@/utils/countryFlags'

interface VisitorsTableProps {
  visitors: AnalyticsVisitorRow[]
  className?: never
}

function truncateHash(hash: string): string {
  return hash.slice(0, 8)
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function deviceIcon(deviceType: string): React.ReactNode {
  switch (deviceType.toLowerCase()) {
    case 'mobile':
      return <Smartphone />
    case 'tablet':
      return <Tablet />
    default:
      return <Monitor />
  }
}

export const VisitorsTable: React.FC<VisitorsTableProps> = ({ visitors }) => (
  <div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.headerCell}>Visitor</th>
          <th className={styles.headerCell}>First Seen</th>
          <th className={[styles.headerCell, styles.headerCenter].join(' ')}>Pages</th>
          <th className={styles.headerCell}>Device</th>
          <th className={styles.headerCell}>Source</th>
          <th className={styles.headerCell}>Country</th>
          <th className={[styles.headerCell, styles.headerCenter].join(' ')}></th>
        </tr>
      </thead>
      <tbody>
        {visitors.length === 0 ? (
          <tr>
            <td colSpan={7} className={styles.empty}>
              No visitors for this period
            </td>
          </tr>
        ) : (
          visitors.map((visitor) => (
            <tr key={visitor.visitor_hash} className={styles.row}>
              <td className={styles.cell}>
                <Link href={`/admin/analytics/${visitor.visitor_hash}`}>
                  <span className={styles.visitorHash}>{truncateHash(visitor.visitor_hash)}</span>
                </Link>
              </td>
              <td className={styles.cell}>
                <span className={styles.timestamp}>{formatTimestamp(visitor.first_seen_at)}</span>
              </td>
              <td className={[styles.cell, styles.cellCenter].join(' ')}>
                <span className={styles.pagesCount}>{visitor.pages_viewed}</span>
              </td>
              <td className={styles.cell}>
                <span className={styles.metaRow}>
                  <span className={styles.metaIcon}>{deviceIcon(visitor.device_type)}</span>
                  <span className={styles.badge}>{visitor.browser}</span>
                  <span className={[styles.badge, styles.badgeMuted].join(' ')}>{visitor.os}</span>
                </span>
              </td>
              <td className={styles.cell}>
                <span className={styles.metaRow}>
                  <span className={styles.metaIcon}><ExternalLink /></span>
                  <span className={styles.timestamp}>{visitor.referrer_source}</span>
                </span>
              </td>
              <td className={styles.cell}>
                <span className={styles.metaRow}>
                  <span className={styles.metaIcon}><Globe /></span>
                  <span className={styles.timestamp}>{countryWithFlag(visitor.country)}</span>
                </span>
              </td>
              <td className={[styles.cell, styles.cellCenter].join(' ')}>
                <Link href={`/admin/analytics/${visitor.visitor_hash}`}>
                  <span className={styles.metaIcon}><ExternalLink /></span>
                </Link>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
    <div className={styles.note}>
      Visitor hashes rotate daily — sessions are only trackable within a single day.
    </div>
  </div>
)
